/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { TDictionary } from "../../../util/types";
import { Contact } from "../../azure-b2c/storage";
import { DeliveryAddress, QuoteCartItem } from "../../cart/storage/cart.types";
import { Order } from "../../products/storage";

export enum QuoteStatus {
  Created = "created",
  Requested = "requested",
  Offered = "offered",
  Cancelled = "cancelled",
  Rejected = "rejected",
  Ordered = "ordered",
  Expired = "expired",
}

export type QuoteStatusType = (typeof QuoteStatus)[keyof typeof QuoteStatus];

export interface QuoteNote {
  name: string;
  role: string;
  message: string;
  sentAt: Date;
}

export interface QuoteDeliveryInfo {
  freightChargeExcGst: number | null;
  pickupStoreId: string | null;
  deliveryAddress: DeliveryAddress | null;
  deliveryInstructions: string | null;
  dairySupplyNumber: string | null;
  isUrgent: boolean | null;
}

export interface Quote {
  id: string;
  reference: string;
  version: string;
  accountNumber: string;
  status: QuoteStatus;
  createdAt: Date;
  expiresOn: Date;
  notes: QuoteNote[];
  contact: Contact;
  delivery: QuoteDeliveryInfo;
  products: QuoteCartItem[];
  isLocked?: boolean;
  order: Order | null;
  declined?: {
    declinedByName: string;
    reason: string;
    notes: string;
    declinedAt: Date;
  };
  offeredBy?: {
    offeredByName: string;
    offeredByEmail: string;
  };
}

export interface CancelQuotePayload {
  version: string;
  accountNumber: string;
}

export interface RejectQuotePayload extends CancelQuotePayload {
  reason: string;
  notes?: string;
}

export interface AcceptQuoteProduct {
  productId: number;
  quantity: number;
  editedPriceExcGst?: number;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
}

export interface QuoteProductInfo extends AcceptQuoteProduct {
  itemId?: string;
  description?: string;
  basePriceExcGst?: number;
  discountedPriceExcGst?: number;
  discountedPriceIncGst?: number;
  originalPriceExcGst?: number;
  editedPriceExcGst?: number;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
}

export interface AcceptQuotePayload extends CancelQuotePayload {
  products: AcceptQuoteProduct[];
  contact: Contact;
  delivery: QuoteDeliveryInfo;
  customerReference: string | null;
  note: string | null;
}

export interface OfferQuotePayload extends Omit<CancelQuotePayload, "accountNumber"> {
  accounts: { accountNumber: string; email: string }[];
  products: QuoteProductInfo[];
  delivery?: QuoteDeliveryInfo;
  isLocked: boolean;
  expiresOn?: Date;
  note: string | null;
}

export interface RejectQuotePayload extends CancelQuotePayload {
  reason: string;
}

export interface CreateColdOfferPayload {
  accounts: { accountNumber: string; email: string }[];
  expiresOn: Date;
  note: string;
  delivery: {
    freightChargeExcGst: number;
  };
  products: QuoteProductInfo[];
  isLocked: boolean;
}

export interface CreateOfferPayload extends CancelQuotePayload {
  note: string;
  expiresOn: Date;
  delivery: QuoteDeliveryInfo;
  products: QuoteProductInfo[];
}

export interface CreateQuotePayload extends CreateOfferPayload {
  contact: Contact;
}

export interface SearchQuotesPayload {
  salesId?: string;
  accountNumber: string;
  skip?: number;
  top?: number;
  startDateTime?: Date;
  endDateTime?: Date;
  expiringSoon?: boolean;
}

export interface SearchQuotesResponse {
  quotes: Quote[];
}

export interface QuotesReducerAction {
  data: Quote[];
  accountNumber: string;
  last?: boolean;
}

export interface QuotesRequestCache {
  status: "pending" | "fulfilled" | "rejected";
  lastRequest: SearchQuotesPayload;
  last?: boolean;
  hash: string;
}

export interface QuotesState {
  quotes: TDictionary<TDictionary<Quote[]>>;
  quotesStatus: TDictionary<TDictionary<QuotesRequestCache>>;
}
